const React = require('react')
const { useState, useEffect } = require('react')
const { useHistory, useLocation } = require('react-router-dom')
const { Button } = require('reactstrap')
const { useBlockchainData, useResponsive, BREAKPOINTS } = require('@hooks')

const { useNetworkConfig } = require('@contexts')

const { LoadingSpinner } = require('@components')
const { UploadContract } = require('./uploadContract.verifyContract-solc-multiple')
const {
    AdvancedConfiguration,
    Constructor,
    Library,
    Success,
    Fail,
    SimilarContract,
    VerifiedContract,
    Title
} = require('@components/verifyContract')
require('@styles/pages/verifyContract/index.css')

const VerifyContractSolcMultiple = () => {
    const history = useHistory()
    const { network } = useNetworkConfig()
    const { verifyContract, getVerificationStatus, getAddressOverview } = useBlockchainData(network.name)
    const location = useLocation()
    const params = new URLSearchParams(location.search)

    const contractAddress = params.get('a')
    const compilerVersion = params.get('c')

    const [files, setFiles] = useState([])

    const [optimization, setOptimization] = useState('No')
    const [optimizationRuns, setOptimizationRuns] = useState(200)
    const [evmVersion, setEvmVersion] = useState('default (compiler defaults)')
    const [licenseType, setLicenseType] = useState(params.get('lictype'))

    const [fetching, setFetching] = useState(false)
    const [data, setData] = useState()
    const [similarContract, setSimilarContract] = useState(null)
    const [verifiedContract, setVerifedContract] = useState(null)

    const [loading, setLoading] = useState(false)

    const [verificationData, setVerificationData] = useState()
    const [isTitleOpen, setIsTitleOpen] = useState(false)
    const [title, setTitle] = useState(null)
    const [isSuccess, setIsSuccess] = useState(false)
    const [isFail, setIsFail] = useState(false)
    const [failData, setFailData] = useState()

    const isMobile = useResponsive(BREAKPOINTS.large)

    const handleVerify = async () => {
        try {
            if (!files.length) {
                setTitle({
                    isSuccess: false,
                    message: 'Please select and upload the solidity files to verify'
                })
                setIsTitleOpen(true)
                return
            }
            setLoading(true)

            const fileContents = await Promise.all(
                files.map(file => {
                    return new Promise(resolve => {
                        const reader = new FileReader()
                        reader.onload = event => {
                            resolve({ [file.name]: { content: event.target.result } })
                        }
                        reader.readAsText(file)
                    })
                })
            )

            const [verifyContractResponse] = await Promise.all([
                verifyContract(
                    contractAddress,
                    JSON.stringify(fileContents),
                    'solidity-multi-part-files',
                    JSON.stringify(files.map(file => file.name)),
                    compilerVersion,
                    optimization === 'Yes' ? true : false,
                    optimizationRuns,
                    evmVersion,
                    licenseType
                )
            ])

            if (verifyContractResponse) {
                setVerificationData(verifyContractResponse)
            }
            setLoading(false)
        } catch (error) {
            console.error('Error verifying contract:', error)
        }
    }

    useEffect(() => {
        const checkStatus = async () => {
            if (!verificationData) return

            setIsSuccess(false)
            setIsFail(false)
            setTitle(null)
            setIsTitleOpen(false)

            const [verificationStatusResponse] = await Promise.all([
                getVerificationStatus(verificationData.verificationId)
            ])

            if (verificationStatusResponse) {
                if (verificationStatusResponse.title === 'Pending') {
                    setTimeout(checkStatus, 1000)
                } else {
                    if (
                        verificationStatusResponse.title === 'Pass - Verified' ||
                        verificationStatusResponse.title === 'Source Code Already Verified'
                    ) {
                        setTitle({
                            isSuccess: true,
                            message: 'Verified'
                        })
                        setIsSuccess(true)
                    } else {
                        setTitle({
                            isSuccess: false,
                            message: verificationStatusResponse.details.message
                        })
                        if (verificationStatusResponse.title !== 'Contract Not Deployed') {
                            setIsFail(true)
                            setFailData({
                                ...verificationStatusResponse.details,
                                bytecode: data?.information.bytecode,
                                compilerVersion,
                                optimization,
                                optimizationRuns
                            })
                        }
                    }
                    setIsTitleOpen(true)
                    setLoading(false)
                }
            }
        }

        checkStatus()
    }, [verificationData])

    useEffect(() => {
        const fetchData = async () => {
            try {
                setFetching(true)
                const [addressResponse] = await Promise.all([getAddressOverview(contractAddress)])

                if (addressResponse) {
                    setData(addressResponse)
                    setSimilarContract(addressResponse.information.contract.similarContract)
                    setVerifedContract(!!addressResponse.information.contract.sourceCode)
                }
                setFetching(false)
            } catch (error) {
                console.error('Error fetching data:', error)
                setFetching(false)
            }
        }
        fetchData()
    }, [contractAddress, isSuccess])

    return (
        <div className='page' style={{ width: isMobile ? '95%' : '60%', margin: '0 auto', maxWidth: '800px' }}>
            <div className='text-center'>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>Verify & Publish Contract Source Code</div>
                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                    Source code verification provides transparency for users interacting with smart contracts. By
                    uploading the source code, Etherscan will match the compiled code with that on the blockchain.&nbsp;
                    <span
                        className={`${network.name}-text-color cursor-pointer`}
                        onClick={() => history.push(`/${network.name}/how-to-verify-contracts`)}
                    >
                        Read more
                    </span>
                    .
                </div>
                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                    This is an&nbsp;<span style={{ fontWeight: 'bold' }}>experimental</span>&nbsp;source code verifier
                    which supports verification of <span style={{ fontWeight: 'bold' }}>multi-part solidity files</span>
                    &nbsp; (imports).
                </div>
            </div>

            <div className='verify-steps-container'>
                <div className='step-item'>
                    <div className={`step-indicator`} style={{ background: `var(--dark-color)` }}>
                        1
                    </div>
                    <div
                        className={`${network.name}-text-color-after cursor-pointer`}
                        onClick={() => {
                            history.push(`/${network.name}/verifycontract?a=${contractAddress}`)
                        }}
                    >
                        Enter Contract Details
                    </div>
                </div>
                <div className='step-divider' />

                <div className='step-item'>
                    <div className={`step-indicator`} style={{ background: `var(--${network.name}-color)` }}>
                        2
                    </div>
                    <div>Verify & Publish</div>
                </div>
            </div>

            {fetching ? (
                <div className='box table-section'>
                    <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                        <LoadingSpinner />
                    </div>
                </div>
            ) : (
                <>
                    {isTitleOpen && <Title title={title} />}
                    {similarContract && <SimilarContract similarContract={similarContract} />}
                    {verifiedContract && <>{isSuccess ? <Success data={data} /> : <VerifiedContract />}</>}
                    {isFail && <Fail data={failData} />}
                    {!similarContract && !verifiedContract && !isFail && (
                        <>
                            <UploadContract files={files} setFiles={setFiles} />
                            <AdvancedConfiguration
                                optimization={optimization}
                                setOptimization={setOptimization}
                                optimizationRuns={optimizationRuns}
                                setOptimizationRuns={setOptimizationRuns}
                                evmVersion={evmVersion}
                                setEvmVersion={setEvmVersion}
                                licenseType={licenseType}
                                setLicenseType={setLicenseType}
                            />
                            <Constructor />
                            <Library />

                            <div className='d-flex justify-content-center gap-3' style={{ margin: '24px 0' }}>
                                <Button
                                    disabled={loading}
                                    onClick={() => {
                                        handleVerify()
                                    }}
                                    style={{
                                        background: `var(--${network.name}-color)`,
                                        border: 'none',
                                        minWidth: '120px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {loading ? 'Verifying...' : 'Verify and Publish'}
                                </Button>
                                <Button
                                    onClick={() => {
                                        window.location.reload()
                                    }}
                                    style={{
                                        background: 'var(--black-background)',
                                        border: 'none',
                                        minWidth: '120px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Reset
                                </Button>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    )
}

module.exports = VerifyContractSolcMultiple
